/**
 * Theme Management Page
 * 
 * Admin page for managing data products and color themes.
 * Provides tabbed interface for product management and theme configuration.
 */

import React, { useState } from 'react';
import {
  Container,
  Header,
  Tabs,
  SpaceBetween,
  Alert,
  Box
} from '@cloudscape-design/components';
import ProductManager from '../../components/admin/ProductManager';
import ThemeManager from '../../components/admin/ThemeManager';

const ThemeManagement: React.FC = () => {
  const [activeTabId, setActiveTabId] = useState<string>('products');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleDataChange = () => {
    // Increment key to force refresh of both components
    setRefreshKey(prev => prev + 1);
  };

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Configure data products, attributes, and color themes for the Live Maps application."
      >
        Theme & Product Management
      </Header>

      <Alert type="info" header="Getting Started">
        <SpaceBetween size="xs">
          <Box variant="p">
            <strong>Products:</strong> Define data products (METAR, Marine, Trail) and their attributes
            (flight category, wind speed, etc.)
          </Box>
          <Box variant="p">
            <strong>Themes:</strong> Create color themes that map data values to colors for visualization
          </Box>
          <Box variant="p">
            <strong>Default Themes:</strong> One theme per product/attribute can be marked as default
          </Box>
          <Box variant="p">
            <strong>Deletion Protection:</strong> Products and attributes with associated themes cannot be deleted
          </Box>
        </SpaceBetween>
      </Alert>

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
        tabs={[
          {
            id: 'products',
            label: 'Data Products',
            content: (
              <Container>
                <ProductManager key={`products-${refreshKey}`} onProductsChange={handleDataChange} />
              </Container>
            )
          },
          {
            id: 'themes',
            label: 'Color Themes',
            content: (
              <Container>
                <ThemeManager key={`themes-${refreshKey}`} onThemesChange={handleDataChange} />
              </Container>
            )
          }
        ]}
      />

      <Alert type="info" header="Need Help?">
        <SpaceBetween size="xs">
          <Box variant="p">
            <strong>Categorical Themes:</strong> Use for discrete text values like flight categories
            (VFR, MVFR, IFR, LIFR). Each category maps to a specific color.
          </Box>
          <Box variant="p">
            <strong>Range-Based Themes:</strong> Use for numeric values like wind speed or temperature.
            Define ranges (e.g., 0-10 kt, 10-20 kt) that map to colors.
          </Box>
          <Box variant="p">
            <strong>Color Format:</strong> Colors must be in hexadecimal format (#RRGGBB), e.g., #22c55e for green.
          </Box>
          <Box variant="p">
            <strong>Theme Application:</strong> Themes are automatically applied to Map Visualization,
            Print Map Maker, and manufacturing files.
          </Box>
        </SpaceBetween>
      </Alert>
    </SpaceBetween>
  );
};

export default ThemeManagement;
